import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Typography,
  Box,
  Button,
  Paper,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { ArrowForward, LocalShipping, Security, Support } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { productService, categoryService } from '../services';
import ProductCard from '../components/ProductCard';
import Loading from '../components/Loading';

const Home = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [newProducts, setNewProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [featuredRes, newRes, categoriesRes] = await Promise.all([
          productService.getFeatured(),
          productService.getNew(),
          categoryService.getAll(),
        ]);

        setFeaturedProducts(featuredRes.data);
        setNewProducts(newRes.data);
        setCategories(categoriesRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) return <Loading />;

  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: { xs: 6, md: 12 },
          mb: 8,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h2" component="h1" gutterBottom fontWeight="bold">
                Welcome to Network Computers
              </Typography>
              <Typography variant="h5" paragraph>
                Discover the latest computer accessories and tech solutions
              </Typography>
              <Typography variant="body1" paragraph sx={{ mb: 4 }}>
                Quality products, competitive prices, and fast delivery. Your one-stop shop
                for all electronic needs.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForward />}
                  onClick={() => navigate('/products')}
                  sx={{ 
                    bgcolor: 'white', 
                    color: 'primary.main',
                    '&:hover': { bgcolor: 'grey.100' }
                  }}
                >
                  Shop Now
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/categories')}
                  sx={{ 
                    borderColor: 'white', 
                    color: 'white',
                    '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' }
                  }}
                >
                  Browse Categories
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                component="img"
                src="/hero-image.svg"
                alt="Electronics"
                sx={{
                  width: '100%',
                  maxWidth: 500,
                  display: 'block',
                  margin: '0 auto',
                }}
                onError={(e) => e.target.style.display = 'none'}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="xl">
        {/* Features */}
        <Grid container spacing={3} sx={{ mb: 8 }}>
          {[
            { icon: <LocalShipping />, title: 'Fast Delivery', text: 'Quick and reliable shipping' },
            { icon: <Security />, title: 'Secure Payment', text: 'Your payment information is safe' },
            { icon: <Support />, title: '24/7 Support', text: 'Dedicated customer support' },
          ].map((feature, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  textAlign: 'center',
                  height: '100%',
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'translateY(-4px)' },
                }}
              >
                <Box sx={{ fontSize: 48, color: 'primary.main', mb: 2 }}>
                  {feature.icon}
                </Box>
                <Typography variant="h6" gutterBottom>
                  {feature.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {feature.text}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Featured Products */}
        {featuredProducts.length > 0 && (
          <Box sx={{ mb: 8 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h4" component="h2" fontWeight="bold">
                Featured Products
              </Typography>
              <Button
                endIcon={<ArrowForward />}
                onClick={() => navigate('/products?featured=true')}
              >
                View All
              </Button>
            </Box>
            <Grid container spacing={3}>
              {featuredProducts.slice(0, isMobile ? 2 : 4).map((product) => (
                <Grid item xs={12} sm={6} md={3} key={product.id}>
                  <ProductCard product={product} />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* New Arrivals */}
        {newProducts.length > 0 && (
          <Box sx={{ mb: 8 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h4" component="h2" fontWeight="bold">
                New Arrivals
              </Typography>
              <Button
                endIcon={<ArrowForward />}
                onClick={() => navigate('/products?isNew=true')}
              >
                View All
              </Button>
            </Box>
            <Grid container spacing={3}>
              {newProducts.slice(0, isMobile ? 2 : 4).map((product) => (
                <Grid item xs={12} sm={6} md={3} key={product.id}>
                  <ProductCard product={product} />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Categories */}
        {categories.length > 0 && (
          <Box sx={{ mb: 8 }}>
            <Typography variant="h4" component="h2" gutterBottom fontWeight="bold" sx={{ mb: 3 }}>
              Popular Categories
            </Typography>
            <Grid container spacing={2}>
              {categories.slice(0, 6).map((category) => (
                <Grid item xs={6} sm={4} md={2} key={category.id}>
                  <Paper
                    onClick={() => navigate(`/products?category=${category.slug}`)}
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4,
                        bgcolor: 'primary.main',
                        color: 'white',
                      },
                    }}
                  >
                    <Typography variant="body2" fontWeight="medium">
                      {category.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {category.product_count} items
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* CTA Section */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            color: 'white',
            borderRadius: 2,
            p: { xs: 4, md: 6 },
            textAlign: 'center',
            mb: 8,
          }}
        >
          <Typography variant="h4" gutterBottom fontWeight="bold">
            Ready to Start Shopping?
          </Typography>
          <Typography variant="body1" paragraph>
            Browse our extensive collection of electronics and accessories
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/products')}
            sx={{ 
              bgcolor: 'white', 
              color: 'primary.main',
              '&:hover': { bgcolor: 'grey.100' }
            }}
          >
            Explore Products
          </Button>
        </Box>
      </Container>
    </>
  );
};

export default Home;
